import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@playcastdotio/Shadcn';
import clsx from 'clsx';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PlaycastReactFunctions } from '../PlaycastWrapper';

// Utility to map KeyboardEvent key to user-friendly/backend-compatible string
function mapKeyToFriendlyString(e: KeyboardEvent): string {
  // Modifier keys (C++ expects these names)
  if (e.key === 'Control') return 'Ctrl';
  if (e.key === 'Alt') return 'Alt';
  if (e.key === 'Shift') return 'Shift';
  if (e.key === 'Meta') return 'Win';

  // Arrow keys (C++ accepts both 'LeftArrow' and 'Left')
  if (e.key === 'ArrowLeft') return 'LeftArrow';
  if (e.key === 'ArrowRight') return 'RightArrow';
  if (e.key === 'ArrowUp') return 'UpArrow';
  if (e.key === 'ArrowDown') return 'DownArrow';

  // C++ also accepts 'Left', 'Right', etc. as synonyms
  if (e.key === 'Left') return 'LeftArrow';
  if (e.key === 'Right') return 'RightArrow';
  if (e.key === 'Up') return 'UpArrow';
  if (e.key === 'Down') return 'DownArrow';

  // Numpad keys
  if (e.location === KeyboardEvent.DOM_KEY_LOCATION_NUMPAD) {
    if (e.key.length === 1 && /[0-9]/.test(e.key)) {
      return `Numpad${e.key}`;
    }
    const numpadMap: Record<string, string> = {
      '+': 'NumpadAdd',
      '-': 'NumpadSubtract',
      '*': 'NumpadMultiply',
      '/': 'NumpadDivide',
      '.': 'NumpadDecimal',
      Enter: 'NumpadEnter',
    };
    if (numpadMap[e.key]) return numpadMap[e.key];
  }

  // Space
  if (e.key === ' ' || e.key === 'Spacebar') return 'Space';

  // Escape
  if (e.key === 'Escape') return 'Esc';

  // Function keys
  if (/^F\d{1,2}$/.test(e.key)) return e.key.toUpperCase();

  // Special and punctuation keys (C++ expects these names)
  const specialMap: Record<string, string> = {
    Backspace: 'Backspace',
    Tab: 'Tab',
    Clear: 'Clear',
    Enter: 'Enter',
    Pause: 'Pause',
    CapsLock: 'CapsLock',
    PageUp: 'PageUp',
    PageDown: 'PageDown',
    End: 'End',
    Home: 'Home',
    Insert: 'Insert',
    Delete: 'Delete',
    PrintScreen: 'PrintScreen',
    ScrollLock: 'ScrollLock',
    NumLock: 'NumLock',
    ContextMenu: 'ContextMenu',
    Help: 'Help',
    ';': 'Semicolon',
    '=': 'Plus',
    ',': 'Comma',
    '-': 'Minus',
    '.': 'Period',
    '/': 'Slash',
    '`': 'Tilde',
    '[': 'OpenBracket',
    '\\': 'Backslash',
    ']': 'CloseBracket',
    "'": 'Quote',
  };
  if (specialMap[e.key]) return specialMap[e.key];

  // Letters and digits (C++ expects uppercase letters, digits as-is)
  if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
    return e.key.toUpperCase();
  }
  if (e.key.length === 1 && /[0-9]/.test(e.key)) {
    return e.key;
  }

  // Fallback: use the key as-is, capitalized (for rare cases)
  return e.key.charAt(0).toUpperCase() + e.key.slice(1);
}

export const RecordKey = ({ isEditable = false, context }: { isEditable?: boolean; context: PlaycastReactFunctions }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [shortcut, setShortcut] = useState(context.stateStores.gameClipConfigState.recordHotkey);
  const [isCapturing, setIsCapturing] = useState(false);
  const [tempShortcut, setTempShortcut] = useState('');
  const [showSave, setShowSave] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEditable) {
      setShortcut(context.stateStores.gameClipConfigState.recordHotkey);
    }
  }, [isEditable, context.stateStores.gameClipConfigState.recordHotkey]);

  const isRecording = useMemo(
    () => context.stateStores.gameClipStatusState.result === 'started',
    [context.stateStores.gameClipStatusState.result],
  );

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
      setIsEditing(false);
    }
  }, []);

  useEffect(() => {
    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing, handleClickOutside]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    e.preventDefault();
    const modifiers: string[] = [];
    if (e.ctrlKey) modifiers.push('Ctrl');
    if (e.altKey) modifiers.push('Alt');
    if (e.shiftKey) modifiers.push('Shift');
    if (e.metaKey) modifiers.push('Win');

    // Map the main key
    const mappedKey = mapKeyToFriendlyString(e);
    if (mappedKey === 'Esc') {
      setIsCapturing(false);
      return;
    }

    // Don't add modifier-only keys
    if (!['Ctrl', 'Alt', 'Shift', 'Win'].includes(mappedKey)) {
      modifiers.push(mappedKey);
    }

    const newShortcut = modifiers.join('-');
    setTempShortcut(newShortcut);
    setShowSave(true);
  }, []);

  useEffect(() => {
    if (isCapturing) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isCapturing, handleKeyDown]);

  const handleSave = useCallback(() => {
    if (tempShortcut) {
      setShortcut(tempShortcut);

      context.targetFunctions.updateGameClipConfig({
        ...context.stateStores.gameClipConfigState,
        recordHotkey: tempShortcut,
      });
    }
    setIsEditing(false);
    setIsCapturing(false);
    setShowSave(false);
  }, [tempShortcut, context]);

  const handleToggleCapture = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCapturing((prev) => !prev);
    setTempShortcut('');
  }, []);

  const handleSaveClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleSave();
    },
    [handleSave],
  );

  const handleEditClick = useCallback(() => {
    if (isEditable) {
      setIsEditing((prev) => !prev);
    }
  }, [isEditable]);

  const tooltipContent = useMemo(
    () => (isEditable ? 'Click to edit shortcut' : `${shortcut} or press the record key to Start/Stop recording.`),
    [isEditable, shortcut],
  );

  const buttonClasses = useMemo(
    () => clsx('flex size-8 items-center justify-center rounded-full text-white title-bar-buttons', !showSave && 'opacity-50'),
    [showSave],
  );

  const containerClasses = useMemo(
    () =>
      clsx(
        'ml-2 flex w-fit items-center rounded-md bg-black title-bar-buttons',
        isEditable ? 'text-md cursor-pointer p-2' : 'p-1 text-xs h-8',
        !isEditable && (isRecording ? 'opacity-100 hover:opacity-80' : 'opacity-60 hover:opacity-50'),
        !isEditable && 'cursor-pointer  active:opacity-100',
        isEditing && '!p-1',
      ),
    [isEditable, isEditing, isRecording],
  );

  const handleRecordKeyClick = useCallback(() => {
    if (isEditable) {
      handleEditClick();
    } else {
      context.targetFunctions.toggleEncoder();
    }
  }, [isEditable, handleEditClick, context]);

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild id='record-key-tooltip-trigger'>
          <div ref={componentRef} className={containerClasses} onClick={handleRecordKeyClick}>
            {isEditing ? (
              <div className='flex items-center gap-2'>
                <div className='cursor-pointer rounded border border-gray-600 px-2 py-1 text-sm' onClick={handleToggleCapture}>
                  {isCapturing ? tempShortcut || 'Press any key...' : shortcut}
                </div>
                <button className={buttonClasses} onClick={handleSaveClick} disabled={!showSave}>
                  <FontAwesomeIcon icon={faFloppyDisk} className='text-emerald-400' />
                </button>
              </div>
            ) : (
              <>
                <div className='m-1 size-2 rounded-full bg-red-600' />
                {`Rec (${shortcut})`}
              </>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent id='record-key-tooltip-content'>
          <p>{tooltipContent}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
